import validatePassword from 'api/utils/passwordStrength'
import classNames from 'classnames'
import { WizardStepProperties } from 'components/types'
import { ChangeEvent, FocusEvent, useEffect, useRef, useState } from 'react'
import { CustomColorsRef, IconSizes } from 'types/general'

import IconRender from '../IconRender'
import FieldErrorMessage from './FieldErrorMessage'
import { fieldInputClass } from './fieldInputStyles'

const PasswordInput = ({
  name,
  label,
  value,
  errorMessage,
  required,
  isFirstInput,
  onChange,
  setIsValid,
  isValidationRequired,
  onBlurExtra,
  showIcon = true,
}: WizardStepProperties & { onBlurExtra?: () => void }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isValidField, setIsValidField] = useState(true)
  const [isFocused, setIsFocused] = useState(false)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, type, value } = e.target
    onChange({ name, type, value })
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    const value = e.target.value
    onBlurExtra?.()

    if (!isValidationRequired) {
      setIsValid?.(true)
      setIsValidField(true)
      return
    }

    if ((!required && value === '') || validatePassword(value)) {
      setIsValidField(true)
      setIsValid?.(true)
    } else {
      setIsValidField(false)
      setIsValid?.(false)
    }
  }

  useEffect(() => {
    if (isFirstInput && inputRef.current) {
      inputRef.current.focus()
    }
    if (!required && value === '') {
      setIsValid?.(true)
    }
  }, [required, value])

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(prev => !prev)
  }

  return (
    <div>
      {label ? (
        <label
          htmlFor={name}
          className="mb-0.5 block pl-1 text-md font-bold text-imovSecondaryDark sm:text-base"
        >
          {label}
        </label>
      ) : null}

      <div className="relative">
        {showIcon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform">
            <IconRender
              icon="regular-lock"
              size={IconSizes.LG}
              color={
                isFocused ? CustomColorsRef.PrimaryLight : CustomColorsRef.Gray
              }
            />
          </span>
        ) : null}
        <input
          type={isPasswordVisible ? 'text' : 'password'}
          ref={inputRef}
          id={name}
          name={name}
          value={value as string}
          onChange={handleChange}
          required={required}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          className={classNames(
            fieldInputClass({ withIcon: showIcon }),
            'pr-10'
          )}
        />

        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-2 top-1/2 -translate-y-1/2 transform text-imovGray hover:text-imovPrimaryLight focus:outline-none"
        >
          {isPasswordVisible ? (
            <IconRender
              icon="regular-eye-slash"
              size={IconSizes.LG}
              color={CustomColorsRef.GrayDark}
            />
          ) : (
            <IconRender
              icon="regular-eye"
              size={IconSizes.LG}
              color={CustomColorsRef.GrayDark}
            />
          )}
        </button>
      </div>
      <FieldErrorMessage
        hasError={!isValidField}
        message={errorMessage}
        className="ml-2"
      />
    </div>
  )
}

export default PasswordInput
